import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import NoticeModal from './NoticeModal';

const HIGH_THRESHOLD = 51000;
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Yüksek çekimler (51.000 TL ve üzeri) — yalnızca yönetim admin/master görür ve onaylar.
const ManagementApprovalPage = () => {
    // Destek ve finansçı dekont kontrolü ve onay yapabilir; admin/yonetim/master tam yetkili.
    const _role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    const canAct = _role === 'admin' || _role === 'master_admin' || _role === 'yonetim' || _role === 'finansci' || _role === 'destek';
    const [items, setItems] = useState([]);
    const [panels, setPanels] = useState([]);
    const [receipt, setReceipt] = useState(null); // görüntülenen dekont
    const [forwardSel, setForwardSel] = useState({}); // { [id]: targetPanelId }
    const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null, danger: false });
    const askConfirm = (message, onConfirm, danger = false) => setConfirmState({ open: true, message, onConfirm, danger });
    const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null, danger: false });
    const [notice, setNotice] = useState({ open: false, message: '', danger: false });
    const showNotice = (message, danger = false) => setNotice({ open: true, message, danger });

    const fetchItems = useCallback(async () => {
        try {
            const res = await api.getWithdrawals({});
            setItems((res.data || []).filter(w => (Number(w.amount) || 0) >= HIGH_THRESHOLD));
        } catch (error) {
            console.error('Yüksek çekimler çekilemedi', error);
        }
    }, []);

    const fetchPanels = async () => {
        try {
            const res = await api.getPanels();
            setPanels(res.data);
        } catch (error) {
            console.error('Paneller çekilemedi', error);
        }
    };

    useEffect(() => {
        document.title = 'PayzoPay - Yüksek Çekimler';
        fetchItems();
        fetchPanels();
        const interval = setInterval(fetchItems, 8000);
        return () => clearInterval(interval);
    }, [fetchItems]);

    const decide = async (id, status) => {
        try {
            // fromMgmt: bu ekran yönetim onay ekranı — yüksek çekimi finalize edebilir.
            await api.updateWithdrawalStatus(id, status, { fromMgmt: true });
            fetchItems();
        } catch (error) {
            showNotice('İşlem başarısız: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const process = async (id) => {
        try {
            await api.updateWithdrawalStatus(id, 'processing', { fromMgmt: true });
            fetchItems();
        } catch (error) {
            showNotice('İşlem başarısız: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const handleReceiptUpload = (id, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.uploadReceipt(id, { receipt: reader.result, name: file.name, type: file.type });
                fetchItems();
                showNotice('Dekont yüklendi.');
            } catch (error) {
                showNotice('Dekont yüklenemedi: ' + (error.response?.data?.error || error.message), true);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleForward = async (id) => {
        const target = forwardSel[id];
        if (!target) { showNotice('Lütfen bir grup seçin.', true); return; }
        const tName = (panels.find(p => p.panelId === target) || {}).name || target;
        askConfirm(`Bu çekimi "${tName}" departmanına taşımak istediğinize emin misiniz?`, async () => {
            try {
                await api.forwardWithdrawal(id, target);
                setForwardSel(prev => ({ ...prev, [id]: '' }));
                fetchItems();
            } catch (error) {
                showNotice('İletme başarısız: ' + (error.response?.data?.error || error.message), true);
            }
        });
    };

    const viewReceipt = async (id) => {
        try {
            const res = await api.getReceipt(id);
            setReceipt(res.data);
        } catch (error) {
            showNotice('Dekont bulunamadı.', true);
        }
    };

    const statusLabel = (s) => s === 'new' ? 'YENİ' : s === 'processing' ? 'İŞLEMDE' : s === 'awaiting_mgmt' ? 'YÖNETİM ONAYINDA' : s === 'approved' ? 'ONAYLANDI' : s === 'rejected' ? 'REDDEDİLDİ' : s;
    const isPdf = receipt && ((receipt.type || '').includes('pdf') || (receipt.receipt || '').startsWith('data:application/pdf'));
    const btn = { padding: '6px 10px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85em' };

    return (
        <div style={{ padding: '20px', color: 'var(--text-color)' }}>
            <h2>Yüksek Çekimler (51.000 TL ve üzeri)</h2>
            <p style={{ opacity: 0.85, marginTop: '-5px', fontSize: '0.9em' }}>51.000 TL ve üzeri çekimler, departman onay tuşuna bastığında buraya düşer. <b>Firma tarafından dekont kontrolü yapıldıktan sonra</b> yalnızca buradan yönetim ve finansçı onaylayabilir.</p>

            <div style={{ background: 'var(--card-bg)', borderRadius: '10px', padding: '20px', boxShadow: 'var(--shadow)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color)' }}>
                            <th style={{ padding: '10px' }}>İsim</th>
                            <th style={{ padding: '10px' }}>Kullanıcı</th>
                            <th style={{ padding: '10px' }}>Banka / IBAN</th>
                            <th style={{ padding: '10px' }}>Tutar</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Durum</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan="6" style={{ padding: '15px', textAlign: 'center', opacity: 0.7 }}>Yüksek çekim yok.</td></tr>
                        ) : items.map(w => (
                            <tr key={w._id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '10px' }}>{w.name}</td>
                                <td style={{ padding: '10px' }}>{w.username}</td>
                                <td style={{ padding: '10px', fontSize: '0.85em' }}>{w.bank}</td>
                                <td style={{ padding: '10px', fontWeight: 'bold' }}>{fmt(w.amount)}</td>
                                <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.85em' }}>{statusLabel(w.status)}</td>
                                <td style={{ padding: '10px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch', minWidth: '220px' }}>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <button onClick={() => viewReceipt(w._id)} style={{ ...btn, background: '#17a2b8' }}>Dekont</button>
                                            {canAct && (
                                            <label style={{ ...btn, background: '#6f42c1', display: 'inline-block' }}>
                                                Dekont Yükle
                                                <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => handleReceiptUpload(w._id, e.target.files[0])} />
                                            </label>
                                            )}
                                            {canAct && w.status === 'new' && (
                                                <button onClick={() => askConfirm('Bu çekimi işleme almak istediğinize emin misiniz?', () => process(w._id))} style={{ ...btn, background: '#007bff' }}>İşleme Al</button>
                                            )}
                                            {canAct && (w.status === 'new' || w.status === 'processing' || w.status === 'awaiting_mgmt') && (
                                                <>
                                                    <button onClick={() => askConfirm('Bu çekimi ONAYLAMAK istediğinize emin misiniz? (Dekont zorunludur)', () => decide(w._id, 'approved'))} style={{ ...btn, background: '#28a745' }}>Onayla</button>
                                                    <button onClick={() => askConfirm('Bu çekimi REDDETMEK istediğinize emin misiniz?', () => decide(w._id, 'rejected'), true)} style={{ ...btn, background: '#dc3545' }}>Reddet</button>
                                                </>
                                            )}
                                        </div>
                                        {canAct && (w.status === 'new' || w.status === 'processing') && (
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <select value={forwardSel[w._id] || ''} onChange={(e) => setForwardSel(prev => ({ ...prev, [w._id]: e.target.value }))} style={{ flex: 1, padding: '5px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-color)', fontSize: '0.8em' }}>
                                                    <option value="">Departmana taşı...</option>
                                                    {panels.filter(p => p.panelId !== w.panelId).map(p => (
                                                        <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                                    ))}
                                                </select>
                                                <button onClick={() => handleForward(w._id)} style={{ ...btn, background: '#fd7e14' }}>Taşı</button>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Dekont görüntüleyici */}
            {receipt && (
                <div onClick={() => setReceipt(null)} style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '16px', borderRadius: '8px', maxWidth: '90%', maxHeight: '90%', overflow: 'auto', textAlign: 'center' }}>
                        <div style={{ marginBottom: '10px', color: '#000', fontWeight: 'bold' }}>{receipt.name || 'Dekont'}</div>
                        {isPdf ? (
                            <iframe title="dekont" src={receipt.receipt} style={{ width: '80vw', height: '75vh', border: 'none' }} />
                        ) : (
                            <img src={receipt.receipt} alt="Dekont" style={{ maxWidth: '80vw', maxHeight: '75vh' }} />
                        )}
                        <div style={{ marginTop: '10px' }}>
                            <a href={receipt.receipt} download={receipt.name || 'dekont'} style={{ marginRight: '12px' }}>İndir</a>
                            <button onClick={() => setReceipt(null)} style={{ padding: '6px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={confirmState.open}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={() => { if (confirmState.onConfirm) confirmState.onConfirm(); closeConfirm(); }}
                onCancel={closeConfirm}
            />

            <NoticeModal
                open={notice.open}
                message={notice.message}
                danger={notice.danger}
                onClose={() => setNotice({ open: false, message: '', danger: false })}
            />
        </div>
    );
};

export default ManagementApprovalPage;
